import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import LoadingButton from '@mui/lab/LoadingButton';
import {
  alpha,
  Autocomplete,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { type FC, useMemo, useState } from 'react';

import {
  type ActivityDimensionName,
  activityDimensionValues,
  type CodeRuleCatalogEntry,
  type CompleteDimensionValues,
  dimensionsFromValues,
  mergeDimensionValues,
  resolveCode,
} from '@okvc/schema';

import { ActivityDimensionPicker } from './ActivityDimensionChips.js';
import { type ActivityEvent, type ReviewActivityEventBody, useReviewActivityEvent } from '../../api/activity.api.js';
import { useAllCodes } from '../../api/codes.api.js';
import useSnackBar from '../../hooks/use-snack-bar.js';
import { type Code } from '../../models/index.js';
import AutocompleteOptions from '../AutocompleteOptions.js';

interface Props {
  event: ActivityEvent;
  onClose: () => void;
  /** Fired after the server stored the review, with the event as it now stands. */
  onReviewed: (event: ActivityEvent) => void;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/** The previous code the capture's resolver saw, so temporal codes preview the way the server stores them. */
function readPreviousCode(metadata: Record<string, unknown>): string | null {
  const resolver = metadata['resolver'];
  const previousCode = isRecord(resolver) ? resolver['previousCode'] : null;
  return typeof previousCode === 'string' ? previousCode : null;
}

function codeLabel(code: Code): string {
  return `${code.code} - ${code.description}`;
}

/**
 * Corrects an AI check-in. The reviewer flips the AI's dimension chips and
 * watches the code the rules resolve to; the picker overrides that code when
 * the rules and the reviewer disagree. Events written before the vocabulary
 * shipped have no chips and fall back to the picker alone.
 */
const ActivityRejectDialog: FC<Props> = ({ event, onClose, onReviewed }) => {
  const { data: codes = [] } = useAllCodes({ includeMetadata: true });
  const review = useReviewActivityEvent(event.id);
  const snackBar = useSnackBar();

  const aiDimensions = event.dimensions ?? null;
  const [values, setValues] = useState<CompleteDimensionValues | null>(
    aiDimensions ? mergeDimensionValues(aiDimensions) : null,
  );
  const [overrideCode, setOverrideCode] = useState<Code | null>(null);
  const [metadataNotes, setMetadataNotes] = useState<Record<string, string>>({});

  const catalog = useMemo<CodeRuleCatalogEntry[]>(
    () => codes.map((code) => ({ code: code.code, rule: code.dimensionRule ?? null })),
    [codes],
  );
  const resolvedCode = useMemo<Code | null>(() => {
    if (!values) {
      return null;
    }
    const previousCode = readPreviousCode(event.metadata);
    const resolved = resolveCode({ dimensions: dimensionsFromValues(values), catalog, previousCode });
    return codes.find((code) => code.code === resolved.code) ?? null;
  }, [values, catalog, codes, event.metadata]);

  const chosenCode = overrideCode ?? resolvedCode;
  const disagrees = Boolean(overrideCode && resolvedCode && overrideCode.id !== resolvedCode.id);
  const pickerHint = disagrees ? 'Differs from the dimensions — your pick is stored and the difference flagged' : ' ';

  const setValue = (name: ActivityDimensionName, value: string) => {
    setValues((current) => current && { ...current, ...activityDimensionValues.parse({ [name]: value }) });
  };

  const submit = async () => {
    if (!chosenCode) {
      return;
    }
    const body: ReviewActivityEventBody = {
      verdict: 'rejected',
      ...(values ? { dimensions: values } : {}),
      ...(overrideCode || !values ? { correctCodeId: chosenCode.id } : {}),
      metadata: Object.entries(metadataNotes)
        .filter(([, notes]) => notes.trim() !== '')
        .map(([codeMetadataId, notes]) => ({ codeMetadataId, notes })),
    };
    try {
      const reviewed = await review.mutateAsync(body);
      snackBar.success('Review saved');
      onReviewed(reviewed);
    } catch {
      snackBar.error('Review failed');
    }
  };

  return (
    <Dialog open onClose={onClose} PaperProps={{ sx: { minWidth: 520 } }}>
      <DialogTitle>Correct AI suggestion</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Box
            sx={{
              px: 1.25,
              py: 0.75,
              borderLeft: '2px solid',
              borderLeftColor: 'primary.main',
              bgcolor: (theme) => alpha(theme.palette.primary.main, 0.06),
              display: 'flex',
              alignItems: 'center',
              gap: 1,
            }}
          >
            <AutoAwesomeIcon fontSize="small" sx={{ color: 'primary.main', opacity: 0.7 }} />
            <Typography variant="body2" sx={{ display: 'flex', alignItems: 'baseline', gap: 0.75 }}>
              <Box component="span" sx={{ color: 'text.secondary' }}>AI suggested:</Box>
              <Box component="span" sx={{ color: 'primary.main', fontWeight: 600 }}>{event.code.code}</Box>
              <Box component="span">{event.code.description}</Box>
            </Typography>
          </Box>

          {values && (
            <ActivityDimensionPicker
              values={values}
              aiValues={mergeDimensionValues(aiDimensions)}
              onChange={setValue}
            />
          )}

          {values && codes.length > 0 && (
            <Typography variant="body2" sx={{ color: resolvedCode ? 'text.primary' : 'warning.main' }}>
              {resolvedCode
                ? <>These dimensions resolve to <strong>{codeLabel(resolvedCode)}</strong></>
                : 'No code matches these dimensions — pick one below'}
            </Typography>
          )}

          <Autocomplete
            value={chosenCode}
            onChange={(_, value: Code | null) => setOverrideCode(value)}
            options={codes}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            getOptionLabel={codeLabel}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Code"
                InputLabelProps={{ required: true }}
                helperText={pickerHint}
              />
            )}
            renderOption={({ key, ...props }, option, { inputValue }) => (
              <AutocompleteOptions option={codeLabel(option)} value={inputValue} key={key} {...props} />
            )}
          />

          {chosenCode?.metadata?.map((metadata) => (
            <TextField
              key={metadata.id}
              label={metadata.prompt}
              multiline={metadata.inputType === 'textarea'}
              value={metadataNotes[metadata.id] ?? ''}
              onChange={(e) => setMetadataNotes((current) => ({ ...current, [metadata.id]: e.target.value }))}
            />
          ))}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <LoadingButton
          variant="contained"
          loading={review.isLoading}
          disabled={!chosenCode}
          onClick={() => void submit()}
        >
          Save
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};

export default ActivityRejectDialog;
